import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiShield, FiUploadCloud, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import styles from './Kyc.module.css';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import axiosClient from '../../api/axiosClient';
import KybSection from '../Kyb/KybSection';
import { useAppSelector } from '../../app/hooks';
import { COUNTRIES, INDIAN_STATES } from '../../data/locations';

type DocCategory = 'identity' | 'address' | 'income';

interface KycDoc {
  id: string;
  document_category: DocCategory;
  document_type: string;
  id_number: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const DOC_OPTIONS: Record<DocCategory, string[]> = {
  identity: ['Govt ID', 'Tax ID', 'Passport', "Driver's License", 'Others'],
  address: ['Bank Statement', 'Mobile Bill', 'Electricity Bill', 'Internet Bill', 'Gas Book', 'Other Utility Bill'],
  income: ['ITR with Computations', 'Transaction Records', 'Other Financial Documents'],
};

const CATEGORY_LABEL: Record<DocCategory, string> = {
  identity: 'Proof of Identity', address: 'Proof of Address', income: 'Proof of Income',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  pending: <FiClock />, approved: <FiCheckCircle />, rejected: <FiXCircle />,
};
const STATUS_COLOR: Record<string, string> = {
  pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a',
};

const Kyc: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [tab, setTab] = useState<'personal' | 'compliance' | 'kyb'>('personal');

  const [personal, setPersonal] = useState({
    title: '', firstName: '', lastName: '', middleName: '', dob: '', gender: '',
    maritalStatus: '', email: '', address: '', city: '', state: '', country: '',
    countryCode: '', contactNo: '', nationality: '',
  });
  const [personalSaving, setPersonalSaving] = useState(false);

  const [myDocs, setMyDocs] = useState<KycDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);

  const [docCategory, setDocCategory] = useState<DocCategory | ''>('');
  const [docType, setDocType] = useState('');
  const [fullName, setFullName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [docDob, setDocDob] = useState('');
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [docSaving, setDocSaving] = useState(false);

  const loadDocs = () => {
    setDocsLoading(true);
    axiosClient.get('/kyc/documents').then(({ data }) => {
      setMyDocs(data.documents);
      setAdminLocked(!!data.adminLocked);
    }).finally(() => setDocsLoading(false));
  };

  // Once we know the status of every category, auto-select whichever one still needs the user's
  // attention — a rejected submission first (highest priority, needs fixing), otherwise a category
  // that was never submitted at all. A pending or already-approved category is left alone, since
  // there's nothing to do there.
  useEffect(() => {
    if (docsLoading || docCategory) return;
    const categories: DocCategory[] = ['identity', 'address', 'income'];
    const rejected = categories.find((cat) => myDocs.some((d) => d.document_category === cat && d.status === 'rejected'));
    if (rejected) { setDocCategory(rejected); return; }
    const neverSubmitted = categories.find((cat) => !myDocs.some((d) => d.document_category === cat));
    if (neverSubmitted) setDocCategory(neverSubmitted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docsLoading, myDocs]);

  // adminLocked: an admin filled this section for the user. The whole section is read-only for the
  // user; he can only send a change request through Support.
  const [adminLocked, setAdminLocked] = useState(false);
  const isPersonalLocked = adminLocked || myDocs.some((d) => d.status === 'approved');
  const CHANGE_REQUEST_URL = '/dashboard/support?subject=' + encodeURIComponent('KYC change request') +
    '&message=' + encodeURIComponent('Please update my KYC / personal details: ');

  useEffect(() => {
    axiosClient.get('/kyc/personal').then(({ data }) => {
      if (data.profile) {
        setPersonal({
          title: data.profile.title || '',
          firstName: data.profile.first_name || '',
          lastName: data.profile.last_name || '',
          middleName: data.profile.middle_name || '',
          dob: data.profile.dob ? data.profile.dob.slice(0, 10) : '',
          gender: data.profile.gender || '',
          maritalStatus: data.profile.marital_status || '',
          email: data.accountEmail || user?.email || '',
          address: data.profile.address || '',
          city: data.profile.city || '',
          state: data.profile.state || '',
          country: data.profile.country || '',
          countryCode: data.profile.country_code || '',
          contactNo: data.profile.contact_no || '',
          nationality: data.profile.nationality || '',
        });
        // Auto-fill the document upload form from the already-saved Personal Details,
        // so the user doesn't have to retype their name and DOB a second time.
        const composedName = [data.profile.first_name, data.profile.middle_name, data.profile.last_name]
          .filter(Boolean).join(' ');
        if (composedName) setFullName(composedName);
        if (data.profile.dob) setDocDob(data.profile.dob.slice(0, 10));
      } else {
        // No profile saved yet — still pre-fill the locked email from the account.
        setPersonal((p) => ({ ...p, email: data.accountEmail || user?.email || '' }));
      }
    }).catch(() => {});
    loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePersonalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPersonalSaving(true);
    try {
      await axiosClient.post('/kyc/personal', personal);
      toast.success('Personal details saved.');
      // Auto-fill the document upload form right away too, without needing a page reload.
      const composedName = [personal.firstName, personal.middleName, personal.lastName].filter(Boolean).join(' ');
      if (composedName) setFullName(composedName);
      if (personal.dob) setDocDob(personal.dob);
      setTab('compliance');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save personal details.');
    } finally {
      setPersonalSaving(false);
    }
  };

  // A category already has an active (pending/approved) submission — block a duplicate resubmit.
  const activeForCategory = (cat: DocCategory) => myDocs.find((d) => d.document_category === cat && d.status !== 'rejected');

  // When the user picks a category to submit for, help them out:
  // - If they're resubmitting a rejected doc for this same category, refill everything from it
  //   (document type options match, so this is a safe like-for-like refill).
  // - Otherwise, only carry over the ID number from their most recent submission as a convenience
  //   default — document type is left blank since each category has a different set of options.
  useEffect(() => {
    if (!docCategory) return;
    const rejectedForCategory = myDocs.find((d) => d.document_category === docCategory && d.status === 'rejected');
    if (rejectedForCategory) {
      if (rejectedForCategory.id_number) setIdNumber(rejectedForCategory.id_number);
      if (rejectedForCategory.document_type) setDocType(rejectedForCategory.document_type);
      return;
    }
    const mostRecentWithId = myDocs.find((d) => d.id_number);
    if (mostRecentWithId?.id_number) setIdNumber(mostRecentWithId.id_number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docCategory]);

  const handleDocSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docCategory || !docType) {
      toast.error('Please select a document type.');
      return;
    }
    const existing = activeForCategory(docCategory);
    if (existing) {
      toast.info(`You already have a ${existing.status} submission for ${CATEGORY_LABEL[docCategory]} — no need to submit again.`);
      return;
    }
    if (docCategory === 'identity' && (!frontFile || !backFile)) {
      toast.error('Please upload both the front and back of your ID.');
      return;
    }
    if (docCategory !== 'identity' && !frontFile) {
      toast.error('Please upload the document.');
      return;
    }

    const formData = new FormData();
    formData.append('fullName', fullName);
    formData.append('idNumber', idNumber);
    formData.append('dob', docDob);
    formData.append('documentCategory', docCategory);
    formData.append('documentType', docType);
    if (docCategory === 'identity') {
      if (frontFile) formData.append('front', frontFile);
      if (backFile) formData.append('back', backFile);
    } else if (frontFile) {
      formData.append('document', frontFile);
    }

    setDocSaving(true);
    try {
      await axiosClient.post('/kyc/documents', formData);
      toast.success('Document submitted — pending review.');
      setDocCategory(''); setDocType(''); setFullName(''); setIdNumber(''); setDocDob('');
      setFrontFile(null); setBackFile(null);
      loadDocs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setDocSaving(false);
    }
  };

  return (
    <DashboardShell>
      <PageHeader
        title="Verification"
        subtitle="Transparency building, trust — complete KYC (individual) and, if applicable, KYB (institutional) verification to continue using our services."
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {!docsLoading && myDocs.length > 0 && (
              <div className={styles.statusList}>
                {myDocs.map((d) => (
                  <div key={d.id} className={styles.statusChip} style={{ borderColor: STATUS_COLOR[d.status], color: STATUS_COLOR[d.status] }}>
                    {STATUS_ICON[d.status]} {CATEGORY_LABEL[d.document_category]} — {d.status}
                  </div>
                ))}
              </div>
            )}
            <WalletBadge />
          </div>
        }
      />
      <div className={styles.page}>
        <div className={styles.tabs}>
          <button className={tab === 'personal' ? styles.tabActive : styles.tab} onClick={() => setTab('personal')}>
            Personal details
          </button>
          <button className={tab === 'compliance' ? styles.tabActive : styles.tab} onClick={() => setTab('compliance')}>
            Kyc &amp; Compliance
          </button>
          <button className={tab === 'kyb' ? styles.tabActive : styles.tab} onClick={() => setTab('kyb')}>
            KYB / Business
          </button>
        </div>

        {tab === 'kyb' && <KybSection />}

        {tab === 'personal' && (
          <form className={styles.card} onSubmit={handlePersonalSubmit}>
            {adminLocked ? (
              <div className={styles.lockBanner}>
                <FiShield /> Your KYC was filled in by an admin, so you cannot change it yourself.{' '}
                <a href={CHANGE_REQUEST_URL}>Send a change request</a> and an admin will update it.
              </div>
            ) : isPersonalLocked && (
              <div className={styles.lockBanner}>
                <FiShield /> Your KYC is approved, so these details are locked.{' '}
                <a href="/dashboard/support">Raise a support ticket</a> if you need to update them.
              </div>
            )}
            <fieldset disabled={isPersonalLocked} className={styles.grid}>
              <div className={styles.field}>
                <label>Title <span>*</span></label>
                <select required value={personal.title} onChange={(e) => setPersonal({ ...personal, title: e.target.value })}>
                  <option value="">-- Select Title --</option>
                  <option>Mr</option><option>Mrs</option><option>Ms</option><option>Dr</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>First Name <span>*</span></label>
                <input required placeholder="Enter first name" value={personal.firstName} onChange={(e) => setPersonal({ ...personal, firstName: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Middle Name</label>
                <input placeholder="Enter middle name (optional)" value={personal.middleName} onChange={(e) => setPersonal({ ...personal, middleName: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Last Name <span>*</span></label>
                <input required placeholder="Enter last name" value={personal.lastName} onChange={(e) => setPersonal({ ...personal, lastName: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Date of Birth <span>*</span></label>
                <input required type="date" value={personal.dob} onChange={(e) => setPersonal({ ...personal, dob: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Gender <span>*</span></label>
                <select required value={personal.gender} onChange={(e) => setPersonal({ ...personal, gender: e.target.value })}>
                  <option value="">-- Select Gender --</option>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Status <span>*</span></label>
                <select required value={personal.maritalStatus} onChange={(e) => setPersonal({ ...personal, maritalStatus: e.target.value })}>
                  <option value="">-- Select Status --</option>
                  <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Email <span>*</span></label>
                <input
                  required type="email" value={personal.email} disabled readOnly
                  style={{ opacity: 0.65, cursor: 'not-allowed' }}
                  title="Your email is locked to your account login email."
                />
              </div>
              <div className={styles.field}>
                <label>Address <span>*</span></label>
                <input required placeholder="Enter address" value={personal.address} onChange={(e) => setPersonal({ ...personal, address: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Country <span>*</span></label>
                <select
                  required
                  value={personal.country}
                  onChange={(e) => setPersonal({ ...personal, country: e.target.value, state: '' })}
                >
                  <option value="">-- Select Country --</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>State <span>*</span></label>
                {personal.country === 'India' ? (
                  <select required value={personal.state} onChange={(e) => setPersonal({ ...personal, state: e.target.value })}>
                    <option value="">-- Select State --</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <input required placeholder="Enter state / province" value={personal.state} onChange={(e) => setPersonal({ ...personal, state: e.target.value })} />
                )}
              </div>
              <div className={styles.field}>
                <label>City <span>*</span></label>
                <input required placeholder="Enter city" value={personal.city} onChange={(e) => setPersonal({ ...personal, city: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Phone Country Code <span>*</span></label>
                <select required value={personal.countryCode} onChange={(e) => setPersonal({ ...personal, countryCode: e.target.value })}>
                  <option value="">-- Select Country Code --</option>
                  <option value="+1">+1 (USA)</option>
                  <option value="+91">+91 (India)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+61">+61 (Australia)</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Contact No <span>*</span></label>
                <input required placeholder="Enter contact number" value={personal.contactNo} onChange={(e) => setPersonal({ ...personal, contactNo: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label>Nationality <span>*</span></label>
                <input required placeholder="Enter nationality" value={personal.nationality} onChange={(e) => setPersonal({ ...personal, nationality: e.target.value })} />
              </div>
            </fieldset>

            <button type="submit" className={styles.submitBtn} disabled={personalSaving || isPersonalLocked}>
              {personalSaving ? 'Saving…' : 'Submit for Verification'}
            </button>

            <div className={styles.infoBox}>
              <FiShield />
              <div>
                <b>Your Security is Our Priority</b>
                <p>All documents are encrypted and processed securely. Your personal information is protected and will only be used for verification purposes.</p>
              </div>
            </div>
          </form>
        )}

        {tab === 'compliance' && (
          <form className={styles.card} onSubmit={handleDocSubmit}>
            {adminLocked && (
              <div className={styles.lockBanner}>
                <FiShield /> Your KYC documents were filled in by an admin, so this section is locked.{' '}
                <a href={CHANGE_REQUEST_URL}>Send a change request</a> and an admin will update it.
              </div>
            )}
            <fieldset disabled={adminLocked} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>Full Name <span>*</span></label>
                <input required placeholder="Enter your full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>ID Number <span>*</span></label>
                <input required placeholder="Enter your ID number" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Date of Birth <span>*</span></label>
                <input required type="date" value={docDob} onChange={(e) => setDocDob(e.target.value)} />
              </div>
              <div className={styles.field}>
                <label>Document Type <span>*</span></label>
                <select
                  required
                  value={docCategory}
                  onChange={(e) => {
                    setDocCategory(e.target.value as DocCategory);
                    setDocType('');
                    setFrontFile(null);
                    setBackFile(null);
                  }}
                >
                  <option value="">Select Document Type</option>
                  <option value="identity">Proof of Identity</option>
                  <option value="address">Proof of Address</option>
                  <option value="income">Proof of Income</option>
                </select>
              </div>
            </div>

            {docCategory && activeForCategory(docCategory) && (
              <div className={styles.savedNote} style={{ marginBottom: 20 }}>
                You already have a <b>{activeForCategory(docCategory)!.status}</b> submission for {CATEGORY_LABEL[docCategory]}. No need to submit again unless it was rejected.
              </div>
            )}

            {docCategory && !activeForCategory(docCategory) && (
              <>
                <div className={styles.field} style={{ marginBottom: 20 }}>
                  <label>Select Document <span>*</span></label>
                  <select required value={docType} onChange={(e) => setDocType(e.target.value)}>
                    <option value="">Select Specific Document</option>
                    {DOC_OPTIONS[docCategory].map((d) => <option key={d}>{d}</option>)}
                  </select>
                </div>

                {docType && (
                  <>
                    <div className={styles.acceptedLabel}>
                      Accepted {CATEGORY_LABEL[docCategory]} Documents
                    </div>
                    <div className={styles.acceptedList}>
                      {DOC_OPTIONS[docCategory].map((d) => <span key={d}># {d}</span>)}
                    </div>

                    {docCategory === 'identity' ? (
                      <div className={styles.uploadGrid}>
                        <div className={styles.field}>
                          <label>Front Side of ID <span>*</span></label>
                          <label className={styles.dropzone}>
                            <FiUploadCloud />
                            <span>{frontFile ? frontFile.name : 'Click to upload front side'}</span>
                            <small>PDF, JPG, or PNG (Max 5MB)</small>
                            <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFrontFile(e.target.files?.[0] || null)} />
                          </label>
                        </div>
                        <div className={styles.field}>
                          <label>Back Side of ID <span>*</span></label>
                          <label className={styles.dropzone}>
                            <FiUploadCloud />
                            <span>{backFile ? backFile.name : 'Click to upload back side'}</span>
                            <small>PDF, JPG, or PNG (Max 5MB)</small>
                            <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setBackFile(e.target.files?.[0] || null)} />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.field}>
                        <label>Upload Document <span>*</span></label>
                        <label className={styles.dropzone}>
                          <FiUploadCloud />
                          <span>{frontFile ? frontFile.name : 'Click to upload or drag and drop'}</span>
                          <small>PDF, JPG, or PNG (Max 5MB)</small>
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFrontFile(e.target.files?.[0] || null)} />
                        </label>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            </fieldset>
            <div className={styles.actionsRow}>
              <button type="button" className={styles.cancelBtn} onClick={() => navigate('/dashboard')}>Cancel</button>
              <button type="submit" className={styles.submitKycBtn} disabled={adminLocked || docSaving || (!!docCategory && !!activeForCategory(docCategory))}>
                {docSaving ? 'Submitting…' : 'Submit KYC'}
              </button>
            </div>
          </form>
        )}
      </div>
    </DashboardShell>
  );
};

export default Kyc;